import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { clsx } from "clsx";
import { Button } from "./Button";
import type { StandardSchemaV1 } from "@tanstack/react-form";
import styles from "./FieldArray.module.css";

type FieldArrayValidate<T> = (value: T, context: { index: number; items: T[] }) => string | null | undefined;

function validateItem<T>(
	value: T,
	index: number,
	items: T[],
	schema?: StandardSchemaV1<T, unknown>,
	validate?: FieldArrayValidate<T>,
): string | null {
	if (schema) {
		const result = schema["~standard"].validate(value);
		if (typeof (result as PromiseLike<unknown>)?.then === "function") {
			throw new Error("Schema must validate synchronously.");
		}
		const issues = (result as { issues?: readonly { message: string }[] }).issues;
		if (issues && issues.length > 0) return issues[0].message;
	}
	return validate?.(value, { index, items }) ?? null;
}

interface FieldArrayContextValue {
	items: unknown[];
	keys: number[];
	errors: (string | null)[];
	disabled: boolean;
	add: (item: unknown) => void;
	setAt: (index: number, item: unknown) => void;
	removeAt: (index: number) => void;
	validateAt: (index: number, value: unknown) => string | null;
}

const FieldArrayContext = createContext<FieldArrayContextValue | null>(null);

function useFieldArrayContext() {
	const context = useContext(FieldArrayContext);
	if (!context) throw new Error("Must be rendered inside <FieldArray.Root>.");
	return context;
}

const FieldArrayItemContext = createContext<number | null>(null);

interface FieldArrayItemState<T = string> {
	value: T;
	index: number;
	setValue: (value: T) => void;
	remove: () => void;
	error: string | null;
	validate: (value: unknown) => string | null;
	isOnly: boolean;

	onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
}

interface KeyState {
	items: readonly unknown[];
	keys: number[];
	nextKey: number;
}

interface PendingKeys {
	base: readonly unknown[];
	next: readonly unknown[];
	keys: number[];
	nextKey: number;
}

/*
 * setSelectionRange throws for input types without selection support (e.g. checkbox, email);
 * those report selectionStart as null.
 */
function moveCaretToEnd(input: HTMLInputElement) {
	if (input.selectionStart !== null) input.setSelectionRange(input.value.length, input.value.length);
}

function sameItems(a: readonly unknown[], b: readonly unknown[]): boolean {
	return a === b || (a.length === b.length && a.every((value, index) => Object.is(value, b[index])));
}

/*
 * given the last committed key state, the incoming items and the latest
 * pending operation, returns the keys (and key counter) belonging to the incoming items.
 */
export function reconcileKeys(state: KeyState, items: readonly unknown[], pending: PendingKeys | null) {
	if (pending && pending.base === state.items && sameItems(items, pending.next)) {
		return { keys: pending.keys, nextKey: pending.nextKey };
	}
	let { keys, nextKey } = state;
	if (keys.length !== items.length) {
		keys = items.map((_, index) => keys[index] ?? nextKey++);
	}
	return { keys, nextKey };
}

function FieldArrayRoot<T = string>({
	items: controlledItems,
	defaultItems,
	onItemsChange,
	schema,
	validate,
	disabled = false,
	className,
	...props
}: React.ComponentProps<"div"> & {
	items?: T[];
	defaultItems?: T[];
	onItemsChange?: (items: T[]) => void;
	schema?: StandardSchemaV1<T, unknown>;
	validate?: FieldArrayValidate<T>;
	disabled?: boolean;
}) {
	const [uncontrolledItems, setUncontrolledItems] = useState<T[]>(defaultItems ?? []);
	const items = controlledItems ?? uncontrolledItems;

	// Row keys follow the *committed* items: add/removeAt never touch keys directly, they only
	// record the intended transformation in pendingRef, adopted once `items` actually reflects that
	// change. A controlled parent may reject or transform an update without desyncing row identity.
	const [keyState, setKeyState] = useState<KeyState>(() => ({
		items,
		keys: items.map((_, index) => index),
		nextKey: items.length,
	}));
	const pendingRef = useRef<PendingKeys | null>(null);

	let { keys, nextKey } = keyState;
	if (items !== keyState.items || keys.length !== items.length) {
		({ keys, nextKey } = reconcileKeys(keyState, items, pendingRef.current));
		setKeyState({ items, keys, nextKey });
	}

	const rootRef = useRef<HTMLDivElement>(null);
	const pendingFocusRef = useRef<number | null>(null);

	// Focus the input of a freshly added row once it exists. Rows are the `[data-slot="field"]`
	// descendants in item order (consumer extras like an add-button row come after the items).
	useEffect(() => {
		const index = pendingFocusRef.current;
		if (index == null) return;
		pendingFocusRef.current = null;
		const input = rootRef.current
			?.querySelectorAll('[data-slot="field"]')
			.item(index)
			?.querySelector('input:not([type="checkbox"]):not([type="radio"])');
		if (input instanceof HTMLInputElement) {
			input.focus();
			moveCaretToEnd(input);
		}
	});

	const update = (next: T[], nextKeys: number[], nextKeyAfter: number) => {
		pendingRef.current = { base: items, next, keys: nextKeys, nextKey: nextKeyAfter };
		if (controlledItems === undefined) setUncontrolledItems(next);
		onItemsChange?.(next);
	};

	const hasValidation = schema !== undefined || validate !== undefined;
	const errors = hasValidation ? items.map((item, index) => validateItem(item, index, items, schema, validate)) : [];

	const value: FieldArrayContextValue = {
		items,
		keys,
		errors,
		disabled,
		add: (item) => {
			pendingFocusRef.current = items.length;
			update([...items, item as T], [...keys, nextKey], nextKey + 1);
		},
		setAt: (index, item) =>
			update(
				items.map((existing, i) => (i === index ? (item as T) : existing)),
				keys,
				nextKey,
			),
		removeAt: (index) =>
			update(
				items.filter((_, i) => i !== index),
				keys.filter((_, i) => i !== index),
				nextKey,
			),
		validateAt: (index, rowValue) => validateItem(rowValue as T, index, items, schema, validate),
	};

	return (
		<FieldArrayContext.Provider value={value}>
			<div
				{...props}
				ref={rootRef}
				className={clsx(styles.root, className)}
				data-slot="field-array"
				data-disabled={disabled || undefined}
				data-invalid={errors.some((error) => error !== null) || undefined}
			/>
		</FieldArrayContext.Provider>
	);
}

function FieldArrayItems<T = string>({ children }: { children: (item: FieldArrayItemState<T>) => ReactNode }) {
	const { items, keys, errors, add, setAt, removeAt, validateAt } = useFieldArrayContext();

	return items.map((item, index) => (
		<FieldArrayItemContext.Provider key={keys[index]} value={index}>
			{children({
				value: item as T,
				index,
				setValue: (next) => setAt(index, next),
				remove: () => removeAt(index),
				error: errors[index] ?? null,
				validate: (rowValue) => validateAt(index, rowValue),
				isOnly: items.length === 1,
				onKeyDown: (event) => {
					if (typeof item !== "string") return;
					if (event.key === "Enter") {
						event.preventDefault(); // a surrounding form must not submit
						if (item.trim() !== "") add("");
						return;
					}
					if (event.key === "Backspace" && item === "" && items.length > 1) {
						event.preventDefault();
						const row = event.currentTarget.closest('[data-slot="field"]');
						const neighborInput = (row?.previousElementSibling ?? row?.nextElementSibling)?.querySelector(
							'input:not([type="checkbox"]):not([type="radio"])',
						);
						if (neighborInput instanceof HTMLInputElement) {
							neighborInput.focus();
							moveCaretToEnd(neighborInput);
						}
						removeAt(index);
					}
				},
			})}
		</FieldArrayItemContext.Provider>
	));
}

function FieldArrayAdd<T = string>({
	disabled,
	onClick,
	item,
	requireValid = false,
	...props
}: Omit<React.ComponentProps<typeof Button>, "type"> & {
	item?: T | (() => T);
	requireValid?: boolean;
}) {
	const context = useFieldArrayContext();
	const blocked = requireValid && context.errors.some((error) => error !== null);

	return (
		<Button
			{...props}
			type="button"
			data-slot="field-array-add"
			disabled={disabled || context.disabled || blocked}
			onClick={(event) => {
				onClick?.(event);
				if (event.defaultPrevented) return;
				context.add(typeof item === "function" ? (item as () => T)() : (item ?? ""));
			}}
		/>
	);
}

function FieldArrayRemove({ disabled, onClick, ...props }: Omit<React.ComponentProps<typeof Button>, "type">) {
	const context = useFieldArrayContext();
	const index = useContext(FieldArrayItemContext);

	if (index === null) {
		throw new Error("Must be rendered inside <FieldArray.Items>.");
	}

	return (
		<Button
			{...props}
			type="button"
			data-slot="field-array-remove"
			disabled={disabled || context.disabled}
			onClick={(event) => {
				onClick?.(event);
				if (event.defaultPrevented) return;
				context.removeAt(index);
			}}
		/>
	);
}

export const FieldArray = {
	Root: FieldArrayRoot,
	Items: FieldArrayItems,
	Add: FieldArrayAdd,
	Remove: FieldArrayRemove,
};

export type { FieldArrayItemState, FieldArrayValidate };
